// 模块页：game × module 默认配置入口 + account × module 运行状态明细。
// - filter（game / module / color）走 URL search params
// - cursor 分页
// - Sheet 内的 batch-command 表单：选择目标 + action + payload_json → 提交后
//   显示 ModuleJobStatus 进度
//
// daemon contract：
//   GET  /api/modules?game=&module=
//   GET  /api/modules/account-matrix?module=&color=&limit=&cursor=
//   POST /api/modules/batch-command form: action, target[]=acc:mod, payload_json
//        - 返回 { ok, job_id }；前端用 /api/module-jobs/{id} 轮询

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { apiGet, apiPostURLSearchParams, APIError } from "@/api/client";
import {
  API_MODULES,
  modulesAccountMatrixPath,
  modulesBatchCommandPath,
} from "@/api/endpoints";
import type {
  GameModuleEntry,
  GameModuleOverviewEntry,
  ModuleMatrixEntry,
  ModulesMatrixResponse,
  ModulesOverviewResponse,
} from "@/api/types";
import { requestConfirmNonce } from "@/api/confirm";
import { QueryView } from "@/components/ui/QueryView";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { StatusBadge, type StatusVariant } from "@/components/ui/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/Sheet";
import { Textarea } from "@/components/ui/Textarea";
import { ModuleJobStatus } from "@/components/jobs/ModuleJobStatus";
import { useSession } from "@/hooks/useSession";
import { toast } from "@/components/ui/Sonner";
import { ConfirmDialog } from "@/components/forms/ConfirmDialog";
import {
  formatModuleConfigDrift,
  formatModuleMatrixColor,
  formatModuleReportedEnabled,
  formatModuleRuntimeStatus,
} from "@/lib/module-status";

export interface ModulesSearch {
  game?: string;
  module?: string;
  color?: string;
  cursor?: number;
}

function parseModulesSearch(search: Record<string, unknown>): ModulesSearch {
  const pick = (v: unknown): string | undefined => {
    if (typeof v !== "string") return undefined;
    const t = v.trim();
    return t === "" ? undefined : t;
  };
  const rawCursor = search.cursor;
  let cursor: number | undefined;
  if (rawCursor !== undefined && rawCursor !== null && rawCursor !== "") {
    const n = Number(rawCursor);
    if (Number.isFinite(n) && n >= 0) cursor = Math.trunc(n);
  }
  return {
    game: pick(search.game),
    module: pick(search.module),
    color: pick(search.color),
    cursor,
  };
}

export const Route = createFileRoute("/modules")({
  validateSearch: parseModulesSearch,
  component: ModulesRouteComponent,
});

function ModulesRouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/modules" });
  return (
    <ModulesView
      search={search}
      onChange={(next) => navigate({ search: next, replace: false })}
    />
  );
}

function buildModulesQuery(search: ModulesSearch): string {
  const params = new URLSearchParams();
  if (search.game) params.set("game", search.game);
  if (search.module) params.set("module", search.module);
  return params.toString();
}

function buildAccountMatrixQuery(search: ModulesSearch): string {
  const params = new URLSearchParams();
  if (search.module) params.set("module", search.module);
  if (search.color) params.set("color", search.color);
  if (typeof search.cursor === "number") {
    params.set("cursor", String(search.cursor));
  }
  return params.toString();
}

function colorToVariant(color: string): StatusVariant {
  switch (color) {
    case "green":
      return "ok";
    case "yellow":
      return "warn";
    case "red":
      return "danger";
    case "drift":
      return "drift";
    default:
      return "neutral";
  }
}

interface ModulesViewProps {
  search: ModulesSearch;
  onChange: (next: ModulesSearch) => void;
}

export function ModulesView({ search, onChange }: ModulesViewProps) {
  const [form, setForm] = useState<ModulesSearch>(search);
  const q = useQuery({
    queryKey: ["modules", search],
    queryFn: () =>
      apiGet<ModulesOverviewResponse>(`${API_MODULES}?${buildModulesQuery(search)}`),
  });
  const matrixQ = useQuery({
    queryKey: ["modules-account-matrix", search],
    queryFn: () =>
      apiGet<ModulesMatrixResponse>(
        `${modulesAccountMatrixPath}?${buildAccountMatrixQuery(search)}`,
      ),
  });

  function apply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onChange({ ...form, cursor: undefined });
  }
  function reset() {
    setForm({});
    onChange({});
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <BatchCommandTrigger modules={q.data?.modules ?? []} />
      </div>

      <form
        onSubmit={apply}
        aria-label="模块过滤器"
        className="grid grid-cols-1 gap-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 sm:grid-cols-5"
      >
        <label className="block text-xs">
          <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
            游戏
          </span>
          <Input
            aria-label="游戏"
            value={form.game ?? ""}
            onChange={(e) =>
              setForm({ ...form, game: e.target.value || undefined })
            }
          />
        </label>
        <label className="block text-xs">
          <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
            模块
          </span>
          <Input
            aria-label="模块"
            value={form.module ?? ""}
            onChange={(e) =>
              setForm({ ...form, module: e.target.value || undefined })
            }
          />
        </label>
        <label className="block text-xs">
          <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
            状态
          </span>
          <Select
            value={form.color ?? "all"}
            onValueChange={(value) =>
              setForm({ ...form, color: value === "all" ? undefined : value })
            }
          >
            <SelectTrigger aria-label="状态">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="green">运行正常</SelectItem>
              <SelectItem value="yellow">待确认</SelectItem>
              <SelectItem value="red">异常</SelectItem>
              <SelectItem value="gray">未运行/未上报</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <div className="flex items-end gap-2 sm:col-span-2">
          <Button type="submit" size="sm">
            应用
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={reset}>
            重置
          </Button>
        </div>
      </form>

      <QueryView query={q} isEmpty={(d) => d.games.length === 0}>
        {(data) => (
          <div className="space-y-4">
            {data.games.map((game) => (
              <GameModulesTable key={game.game_id} game={game} />
            ))}
          </div>
        )}
      </QueryView>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-medium">账号运行态明细</h2>
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
            这里保留账号 x 模块矩阵，用于查看任务运行、配置一致性和批量查询、下发、启停。
          </p>
        </div>
        <QueryView query={matrixQ} isEmpty={(d) => d.rows.length === 0}>
          {(data) => (
            <>
              <Table dense>
                <TableHeader>
                  <TableRow>
                    <TableHead>账号 ID</TableHead>
                    <TableHead>模块</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>上报开关</TableHead>
                    <TableHead>运行状态</TableHead>
                    <TableHead>配置一致性</TableHead>
                    <TableHead>最近错误</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((row: ModuleMatrixEntry) => (
                    <TableRow key={`${row.account_id}:${row.module}`}>
                      <TableCell className="font-mono">{row.account_id}</TableCell>
                      <TableCell className="font-mono">{row.module}</TableCell>
                      <TableCell>
                        <StatusBadge variant={colorToVariant(row.color_state)}>
                          {formatModuleMatrixColor(row.color_state)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        {formatModuleReportedEnabled(
                          row.desired_enabled,
                          row.runtime_status,
                        )}
                      </TableCell>
                      <TableCell>{formatModuleRuntimeStatus(row.runtime_status)}</TableCell>
                      <TableCell>{formatModuleConfigDrift(row.config_drift)}</TableCell>
                      <TableCell className="text-[hsl(var(--muted-foreground))]">
                        {row.last_command_error ?? ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {data.next_cursor ? (
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      onChange({ ...search, cursor: data.next_cursor })
                    }
                  >
                    下一页
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </QueryView>
      </section>
    </div>
  );
}

function GameModulesTable({ game }: { game: GameModuleOverviewEntry }) {
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-medium">
            {game.display_name || game.game_id}
          </h2>
          <p className="mt-1 font-mono text-xs text-[hsl(var(--muted-foreground))]">
            {game.game_id} / {game.account_count} 个账号
          </p>
        </div>
        <Button asChild size="sm" variant="outline">
          <Link
            to="/games/$gameId"
            params={{ gameId: game.game_id }}
            search={{ tab: "modules" }}
          >
            打开游戏模块
          </Link>
        </Button>
      </div>
      <Table dense>
        <TableHeader>
          <TableRow>
            <TableHead>模块</TableHead>
            <TableHead>游戏覆盖</TableHead>
            <TableHead>加载状态</TableHead>
            <TableHead>账号覆盖</TableHead>
            <TableHead>实例覆盖</TableHead>
            <TableHead>运行态</TableHead>
            <TableHead>操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {game.modules.map((module) => (
            <GameModuleRow
              key={`${game.game_id}:${module.module}`}
              gameID={game.game_id}
              module={module}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function GameModuleRow({
  gameID,
  module,
}: {
  gameID: string;
  module: GameModuleEntry;
}) {
  return (
    <TableRow>
      <TableCell className="font-mono">{module.module}</TableCell>
      <TableCell>
        {module.game_override ? (
          <StatusBadge variant="ok">已配置</StatusBadge>
        ) : (
          <StatusBadge variant="neutral">继承默认</StatusBadge>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge variant={moduleGameLoaded(module) ? "ok" : "neutral"}>
          {moduleGameLoaded(module) ? "加载" : "不加载"}
        </StatusBadge>
      </TableCell>
      <TableCell>{module.account_override_count}</TableCell>
      <TableCell>{module.instance_override_count}</TableCell>
      <TableCell className="text-xs">
        <span>{module.runtime.running} 运行中</span>
        <span className="ml-2 text-[hsl(var(--muted-foreground))]">
          {module.runtime.drift} 配置偏差
        </span>
      </TableCell>
      <TableCell>
        <Button asChild size="sm" variant="outline">
          <Link
            to="/games/$gameId"
            params={{ gameId: gameID }}
            search={{ tab: "modules" }}
          >
            编辑默认
          </Link>
        </Button>
      </TableCell>
    </TableRow>
  );
}

function moduleGameLoaded(module: GameModuleEntry) {
  if (!module.game_override) return true;
  const gameOverride = parseJSONRecord(module.game_override_json || "{}");
  if (typeof gameOverride.load === "boolean") {
    return gameOverride.load;
  }
  if (typeof module.effective_config?.load === "boolean") {
    return module.effective_config.load;
  }
  return true;
}

function parseJSONRecord(raw: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(raw || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return {};
  }
  return {};
}

interface BatchCommandTriggerProps {
  modules: string[];
}

function BatchCommandTrigger({ modules }: BatchCommandTriggerProps) {
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const actor = session.data?.actor ?? "anonymous";
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [action, setAction] = useState("enable");
  const [targets, setTargets] = useState(""); // 一行一个 account_id:module
  const [payload, setPayload] = useState("{}");
  const [jobID, setJobID] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [serverConfirm, setServerConfirm] = useState<{
    nonce: string;
    target: string;
  } | null>(null);

  function parsedTargets(): string[] {
    return targets
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  async function submit(confirm?: { nonce: string; target: string }) {
    const list = parsedTargets();
    if (list.length === 0) {
      toast.error("至少输入一行 target（格式 account_id:module）");
      return;
    }
    setPending(true);
    try {
      const body = new URLSearchParams();
      body.set("action", action);
      body.set("payload_json", payload);
      for (const t of list) body.append("target", t);
      if (confirm) {
        body.set("confirm_target", confirm.target);
        body.set("confirm_nonce", confirm.nonce);
        body.set("confirm_text", confirm.target);
      }
      const data = await apiPostURLSearchParams<{ ok: boolean; job_id?: string }>(
        modulesBatchCommandPath,
        body,
      );
      if (!data.ok || !data.job_id) {
        throw new Error("缺少 job_id");
      }
      setJobID(data.job_id);
      toast.success(`已派发 ${list.length} 个目标 (job ${data.job_id})`);
      void queryClient.invalidateQueries({ queryKey: ["modules"] });
      void queryClient.invalidateQueries({ queryKey: ["modules-account-matrix"] });
    } catch (err) {
      const msg =
        err instanceof APIError ? err.message : err instanceof Error ? err.message : String(err);
      toast.error("batch-command 失败", { description: msg });
    } finally {
      setPending(false);
    }
  }

  function handleSubmit() {
    const count = parsedTargets().length;
    if (count > 20) {
      const target = "module-batch";
      setPending(true);
      requestConfirmNonce(`module_batch:${target}`, target)
        .then((res) => {
          setServerConfirm({ nonce: res.nonce, target: res.target });
          setConfirmOpen(true);
        })
        .catch((err) => {
          const msg =
            err instanceof APIError
              ? err.message
              : err instanceof Error
                ? err.message
                : String(err);
          toast.error("申请确认 nonce 失败", { description: msg });
        })
        .finally(() => setPending(false));
      return;
    }
    setServerConfirm(null);
    setConfirmOpen(true);
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size="sm" disabled={!canWrite}>
          批量命令
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>模块批量命令</SheetTitle>
        </SheetHeader>
        <div className="space-y-3">
          <label className="block text-xs">
            <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
              action
            </span>
            <Input
              aria-label="action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="query_status / apply_config / start / stop"
            />
          </label>
          <label className="block text-xs">
            <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
              targets（每行 account_id:module，可选模块从下方列表复制）
            </span>
            <Textarea
              aria-label="targets"
              rows={6}
              value={targets}
              onChange={(e) => setTargets(e.target.value)}
            />
            <div className="mt-1 flex flex-wrap gap-1">
              {modules.map((m) => (
                <span
                  key={m}
                  className="rounded border border-[hsl(var(--border))] px-1.5 py-0.5 font-mono text-[10px] text-[hsl(var(--muted-foreground))]"
                >
                  {m}
                </span>
              ))}
            </div>
          </label>
          <label className="block text-xs">
            <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
              payload_json
            </span>
            <Textarea
              aria-label="payload_json"
              rows={3}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
            />
          </label>
          {jobID ? <ModuleJobStatus jobID={jobID} /> : null}
        </div>
        <SheetFooter>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={pending}
          >
            关闭
          </Button>
          <Button size="sm" disabled={pending} onClick={handleSubmit}>
            {pending ? "提交中…" : "派发"}
          </Button>
        </SheetFooter>
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          resource="module_batch"
          action={action}
          target={serverConfirm?.target ?? `${parsedTargets().length}-targets`}
          actor={actor}
          description={`对 ${parsedTargets().length} 个目标执行 ${action}`}
          sig={serverConfirm?.nonce}
          onConfirm={() => submit(serverConfirm ?? undefined)}
        />
      </SheetContent>
    </Sheet>
  );
}
